import { useState, useCallback, useRef, useEffect } from 'react';
import { PRESENTER_COMMENTS_AUTO_CLOSE_DELAY_MS } from '../constants/presenterComments';

/**
 * Comments panel open / pin / hover-auto-close behavior for Presenter modal.
 */
export function useCommentsPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const resetPanel = useCallback(() => {
    clearCloseTimer();
    setIsOpen(false);
    setIsPinned(false);
  }, [clearCloseTimer]);

  const openPanel = useCallback(() => {
    clearCloseTimer();
    setIsOpen(true);
  }, [clearCloseTimer]);

  const closePanel = useCallback(() => {
    clearCloseTimer();
    setIsOpen(false);
    setIsPinned(false);
  }, [clearCloseTimer]);

  const togglePanel = useCallback(() => {
    if (isOpen) {
      closePanel();
    } else {
      openPanel();
    }
  }, [isOpen, openPanel, closePanel]);

  const pinPanel = useCallback(() => {
    clearCloseTimer();
    setIsPinned(true);
  }, [clearCloseTimer]);

  const togglePin = useCallback(() => {
    clearCloseTimer();
    setIsPinned((prev) => !prev);
  }, [clearCloseTimer]);

  const handlePanelMouseEnter = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  const handlePanelMouseLeave = useCallback(() => {
    if (isPinned || !isOpen) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, PRESENTER_COMMENTS_AUTO_CLOSE_DELAY_MS);
  }, [isPinned, isOpen, clearCloseTimer]);

  const handleCommentsButtonMouseEnter = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return {
    isOpen,
    isPinned,
    openPanel,
    closePanel,
    togglePanel,
    togglePin,
    pinPanel,
    resetPanel,
    handlePanelMouseEnter,
    handlePanelMouseLeave,
    handleCommentsButtonMouseEnter,
  };
}
